/**
 * RunMonitoring: fleet-wide "Run health and replay" admin page (OBS-6 B).
 * Distinct from ObservabilityPage (daemon anomalies, Spec 62).
 *
 * Shows: health stats + cost outlier table + latency outlier table.
 * Gated: CanManageWorkflows. Route: /admin/run-monitoring.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, Clock, DollarSign, RefreshCw, TrendingUp, Zap } from 'lucide-react';
import {
  getRunHealthAggregatesOptions,
  listLlmCallCostOutliersOptions,
  listLlmCallLatencyOutliersOptions,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { LlmCallOutlierRow, RunHealthAggregates } from '../../generated/api/types.gen.js';
import { Button, Callout, DataTable, EmptyState, FilterPills, Skeleton } from '../../components/ui/index.js';
import type { DataTableColumn, StatStripCell } from '../../components/ui/index.js';
import { DashboardGrid, DashboardPage } from '../../components/page/DashboardPage.js';
import { PAGE_HELP } from '../../help/index.js';
import { humanize } from './admin-format.js';

// ─── Time window options ──────────────────────────────────────────────────────

interface TimeWindow {
  id: string;
  label: string;
  hours: number;
}

const TIME_WINDOWS: TimeWindow[] = [
  { id: '1h', label: 'Last hour', hours: 1 },
  { id: '6h', label: '6 hours', hours: 6 },
  { id: '24h', label: '24 hours', hours: 24 },
  { id: '7d', label: '7 days', hours: 24 * 7 },
  { id: '30d', label: '30 days', hours: 24 * 30 },
];

function windowParams(hours: number): { since: string; until: string } {
  const until = new Date();
  const since = new Date(until.getTime() - hours * 60 * 60 * 1000);
  return { since: since.toISOString(), until: until.toISOString() };
}

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtCost(v: string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(4)}`;
}

function fmtCostTotal(v: string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function fmtDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── Health strip ─────────────────────────────────────────────────────────────

function buildHealthCells(agg: RunHealthAggregates): StatStripCell[] {
  const { llmCalls } = agg;
  const errorRate = llmCalls.totalCalls === 0 ? null : (llmCalls.errorCalls / llmCalls.totalCalls) * 100;

  return [
    {
      label: 'Total calls',
      value: llmCalls.totalCalls.toLocaleString(),
      icon: Activity,
    },
    {
      label: 'Error rate',
      value: errorRate == null ? '—' : `${errorRate.toFixed(1)}%`,
      severity:
        errorRate != null && errorRate > 10 ? 'danger' : errorRate != null && errorRate > 2 ? 'warning' : undefined,
      sub: `${llmCalls.errorCalls} errors, ${llmCalls.nonStopFinishes} did not finish normally`,
      icon: Zap,
    },
    {
      label: 'Average latency',
      value: fmtDuration(llmCalls.avgDurationMs),
      sub: `Median ${fmtDuration(llmCalls.p50DurationMs)}`,
      icon: Clock,
    },
    {
      label: 'Total cost',
      value: fmtCostTotal(llmCalls.totalCostUsd),
      sub: `${fmtCost(llmCalls.avgCostUsd)} per call`,
      icon: DollarSign,
    },
  ];
}

// ─── Outlier table columns ────────────────────────────────────────────────────

const costColumns: DataTableColumn<LlmCallOutlierRow>[] = [
  {
    key: 'startedAt',
    header: 'Started',
    width: '140px',
    render: (row) => <span className="font-mono text-[11.5px] text-muted">{fmtDateTime(row.startedAt)}</span>,
  },
  {
    key: 'model',
    header: 'Model',
    render: (row) => (
      <span className="text-body">
        {row.model}
        {row.modelVersion && <span className="ml-1 text-[11px] text-faint">{row.modelVersion}</span>}
      </span>
    ),
  },
  {
    key: 'tokens',
    header: 'Tokens',
    width: '110px',
    align: 'right',
    render: (row) => (
      <span className="font-mono text-[11.5px] text-muted">
        {row.promptTokens != null ? row.promptTokens.toLocaleString() : '—'} /&nbsp;
        {row.completionTokens != null ? row.completionTokens.toLocaleString() : '—'}
      </span>
    ),
  },
  {
    key: 'costUsd',
    header: 'Cost',
    width: '90px',
    align: 'right',
    sortable: true,
    sortValue: (row) => (row.costUsd != null ? parseFloat(row.costUsd) : null),
    render: (row) => <span className="font-mono text-[11.5px] font-medium text-heading">{fmtCost(row.costUsd)}</span>,
  },
  {
    key: 'runId',
    header: 'Run',
    width: '130px',
    render: (row) =>
      row.runId ? (
        <Link
          to={`/agents/runs/${row.runId}`}
          className="font-mono text-[11px] text-accent-text hover:underline truncate block max-w-[120px]"
        >
          {row.runId.slice(0, 12)}…
        </Link>
      ) : (
        <span className="text-faint font-mono text-[11px]">—</span>
      ),
  },
];

const latencyColumns: DataTableColumn<LlmCallOutlierRow>[] = [
  {
    key: 'startedAt',
    header: 'Started',
    width: '140px',
    render: (row) => <span className="font-mono text-[11.5px] text-muted">{fmtDateTime(row.startedAt)}</span>,
  },
  {
    key: 'model',
    header: 'Model',
    render: (row) => (
      <span className="text-body">
        {row.model}
        {row.modelVersion && <span className="ml-1 text-[11px] text-faint">{row.modelVersion}</span>}
      </span>
    ),
  },
  {
    key: 'durationMs',
    header: 'Latency',
    width: '90px',
    align: 'right',
    sortable: true,
    sortValue: (row) => row.durationMs,
    render: (row) => (
      <span className="font-mono text-[11.5px] font-medium text-heading">{fmtDuration(row.durationMs)}</span>
    ),
  },
  {
    key: 'tokens',
    header: 'Tokens',
    width: '110px',
    align: 'right',
    render: (row) => (
      <span className="font-mono text-[11.5px] text-muted">
        {row.promptTokens != null ? row.promptTokens.toLocaleString() : '—'} /&nbsp;
        {row.completionTokens != null ? row.completionTokens.toLocaleString() : '—'}
      </span>
    ),
  },
  {
    key: 'finishReason',
    header: 'Finish',
    width: '90px',
    render: (row) => (
      <span className="text-[11.5px] text-muted">{row.finishReason ? humanize(row.finishReason) : '—'}</span>
    ),
  },
  {
    key: 'runId',
    header: 'Run',
    width: '130px',
    render: (row) =>
      row.runId ? (
        <Link
          to={`/agents/runs/${row.runId}`}
          className="font-mono text-[11px] text-accent-text hover:underline truncate block max-w-[120px]"
        >
          {row.runId.slice(0, 12)}…
        </Link>
      ) : (
        <span className="text-faint font-mono text-[11px]">—</span>
      ),
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface OutlierTableProps {
  title: string;
  rows: LlmCallOutlierRow[] | undefined;
  columns: DataTableColumn<LlmCallOutlierRow>[];
  isLoading: boolean;
  isError: boolean;
  hasMore?: boolean;
}

const OutlierTable: React.FC<OutlierTableProps> = ({ title, rows, columns, isLoading, isError, hasMore }) => {
  const help = PAGE_HELP['run-monitoring'];
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-heading">{title}</h2>
      {isError ? (
        <Callout tone="danger" icon={AlertTriangle} title="Could not load these calls">
          Check your connection and try again.
        </Callout>
      ) : (
        <DataTable
          framed
          columns={columns}
          rows={rows ?? []}
          rowKey={(row) => row.id}
          loading={isLoading}
          aria-label={title}
          footer={
            hasMore ? <span className="text-xs text-muted">More calls match than are shown here.</span> : undefined
          }
          empty={
            <EmptyState
              icon={TrendingUp}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
            />
          }
        />
      )}
    </section>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const RunMonitoringPage: React.FC = () => {
  const help = PAGE_HELP['run-monitoring'];
  const [windowId, setWindowId] = useState('24h');
  // The window's bounds are part of the query key, so they are fixed per selection (or refresh), not per render.
  const [range, setRange] = useState(() => windowParams(24));

  const select = (id: string) => {
    setWindowId(id);
    setRange(windowParams((TIME_WINDOWS.find((w) => w.id === id) ?? TIME_WINDOWS[2]).hours));
  };
  const refresh = () => select(windowId);

  const healthQuery = useQuery(getRunHealthAggregatesOptions({ query: range }));
  const costQuery = useQuery(listLlmCallCostOutliersOptions({ query: { ...range, limit: 25 } }));
  const latencyQuery = useQuery(listLlmCallLatencyOutliersOptions({ query: { ...range, limit: 25 } }));

  const stats: StatStripCell[] = healthQuery.data
    ? buildHealthCells(healthQuery.data)
    : ['Total calls', 'Error rate', 'Average latency', 'Total cost'].map((label) => ({
        label,
        value: healthQuery.isError ? '—' : <Skeleton height={22} width="65%" rounded="rounded" />,
      }));

  return (
    <DashboardPage
      title="Run health and replay"
      subtitle="LLM call health across every workflow run, with the costliest and slowest calls."
      helpContent={help}
      actions={
        <>
          <FilterPills
            aria-label="Time window"
            options={TIME_WINDOWS.map((w) => ({ id: w.id, label: w.label }))}
            value={windowId}
            onChange={select}
          />
          <Button variant="secondary" size="sm" onClick={refresh}>
            <RefreshCw size={14} aria-hidden="true" />
            Refresh
          </Button>
        </>
      }
      stats={stats}
      alert={
        healthQuery.isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load health figures"
            actions={
              <Button size="sm" variant="secondary" onClick={() => healthQuery.refetch()}>
                Retry
              </Button>
            }
          >
            The server returned an error. Check that you have permission to manage workflows.
          </Callout>
        ) : undefined
      }
    >
      <DashboardGrid>
        <div className="xl:col-span-6">
          <OutlierTable
            title="Costliest calls"
            rows={costQuery.data?.data}
            columns={costColumns}
            isLoading={costQuery.isPending}
            isError={costQuery.isError}
            hasMore={costQuery.data?.paging.hasMore}
          />
        </div>
        <div className="xl:col-span-6">
          <OutlierTable
            title="Slowest calls"
            rows={latencyQuery.data?.data}
            columns={latencyColumns}
            isLoading={latencyQuery.isPending}
            isError={latencyQuery.isError}
            hasMore={latencyQuery.data?.paging.hasMore}
          />
        </div>
      </DashboardGrid>
    </DashboardPage>
  );
};

export default RunMonitoringPage;
